import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  ButtonGroup,
  Button,
  Divider,
  List,
  Theme
} from '@material-ui/core';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import AddIcon from '@material-ui/icons/Add';
import { Navigation, Modal, Popover } from './components';
import ShareRepositoryDialog from 'views/Repository/components/ShareRepositoryDialog'
import SharedRepoOwnerDTO from 'dto/SharedRepoOwnerDTO';
import Repository from 'services/repositories';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    backgroundColor: theme.palette.white
  },
  toolbar: {
    padding: theme.spacing(2, 0),
    textAlign: 'center',
    height: 68
  },
  addIcon: {
    marginRight: theme.spacing(1)
  },
  addButton: {
    maxWidth: 231
  },
  button: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    fontWeight: 400
  },
  folderIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.icon
  },
  totalItems: {
    marginLeft: theme.spacing(1)
  },
  newItems: {
    marginLeft: 'auto'
  },
  list: {
    height: 'calc(100% - 69px)',
    overflowY: 'auto'
  },
  active: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    '& $folderIcon': {
      color: theme.palette.primary.main
    }
  }
}));

const Folders = props => {
  const { className, folders, createFolder, deleteFolder, create, duplicate, onFolderOpen, onDropFolder, ...rest } = props;

  const classes = useStyles();
  const newFolderRef = useRef(null);
  const [openPopover, setOpenPopover] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  
  const [openShare, setOpenShare] = useState<SharedRepoOwnerDTO | null>(null);
  const [shared, setShared] = useState<Record<number, SharedRepoOwnerDTO>>();

  const handleSelect = (parent) => (repository) => {
    onFolderOpen && onFolderOpen(repository, parent);
  };

  const handleOverDrop = (folder) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDropFolder && folder !== Number(e.dataTransfer.getData('folder'))) {
      onDropFolder(folder, Number(e.dataTransfer.getData('id')));
      if (e.clear) e.clear();
    }
  }

  const handleShare = (repoID: number) => () => {
    if(!shared || !repoID) {
      return;
    }

    setOpenShare(shared[repoID]);
  }

  const getShared = async () => {
    const data = await Repository.getReposIShare();
    setShared(data.reduce((acc, curr) => ({
      ...acc,
      [curr.repoID]: curr
    }), {}));
    
    return data;
  }

  React.useEffect(() => {
    getShared();
  }, [folders.length]);

  const refreshDialog = async (reopen?: boolean) => {
    const data = await getShared();
    reopen && setOpenShare(data.find(repo => repo.repoID === openShare.repoID))
    reopen && console.log('set open share')
  }

  return (
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >
      <div className={classes.toolbar}>
        <ButtonGroup ref={newFolderRef}>
          <Button
            className={classes.addButton}
            color="primary"
            fullWidth
            onClick={() => setOpenPopover(true)}
            variant="contained"
          >
            <CreateNewFolderIcon className={classes.addIcon} />
            Folder
          </Button>
          <Button
            className={classes.addButton}
            color="primary"
            fullWidth
            onClick={() => setOpenModal(true)}
            variant="contained"
          >
            <AddIcon className={classes.addIcon} />
            Repository
          </Button>
        </ButtonGroup>
      </div>
      <Divider />
      <List
        className={classes.list}
        onDragOver={e => e.preventDefault()}
        onDrop={handleOverDrop(0)}
      >
        <Navigation
          component="div"
          deleteFolder={deleteFolder}
          handleDuplicate={duplicate}
          handleOverDrop={handleOverDrop}
          handleSelect={handleSelect}
          handleShare={handleShare}
          onOpenModal={setOpenModal}
          pages={folders}
          shared={shared}
        />
      </List>
      <Modal
        onClose={() => setOpenModal(false)}
        onSubmit={create}
        open={openModal}
      />
      <Popover
        anchorEl={newFolderRef.current}
        arrow
        onClose={() => setOpenPopover(false)}
        onSubmit={createFolder}
        open={openPopover}
        title="Add new folder"
      />
      <ShareRepositoryDialog
        handleRefresh={refreshDialog}
        onClose={() => setOpenShare(null)}
        open={!!openShare}
        repo={openShare}
      />
    </div>
  );
};

Folders.propTypes = {
  className: PropTypes.string,
  create: PropTypes.func,
  createFolder: PropTypes.func,
  deleteFolder: PropTypes.func,
  duplicate: PropTypes.func,
  folders: PropTypes.array,
  onDropFolder: PropTypes.func,
  onFolderOpen: PropTypes.func
};

export default Folders;
